import type { FC } from 'react';
import * as React from 'react';
import parse from 'html-react-parser';

import { cn } from '@/lib/utils';

import type { CtaProps } from '@/components/atoms/Cta';
import Cta from '@/components/atoms/Cta';
import { ImageLink } from '@/components/atoms/ImageLink';
import { P } from '@/components/atoms/Typography/P';
import {
    ButtonsFactory,
    type ButtonsFactoryProps,
} from '@/components/molecules/ButtonsFactory';
import type { ContentTextProps } from '@/components/molecules/ContentText';
import { ContentText } from '@/components/molecules/ContentText';
import { IconPointerList } from '@/components/molecules/IconPointerList';
import type { IconPointerListType } from '@/components/molecules/IconPointerList/IconPointerList';
import {
    IconsLinksList,
    type IconsLinksListProps,
} from '@/components/molecules/IconsLinksList';
import type { RichTextProps } from '@/components/molecules/RichText';
import { RichText } from '@/components/molecules/RichText';
import ShowMore from '@/components/molecules/ShowMore';
import type { PromoCodeProps } from '@/components/templates/IconPointerListContent/components/PromoCode';
import PromoCode from '@/components/templates/IconPointerListContent/components/PromoCode';

export interface IconPointerListContentProps {
    isLogotypeVisible?: boolean;
    logotypeUrl?: string;
    contentTextConfig: ContentTextProps;
    description?: RichTextProps['field'];
    listData: IconPointerListType['listData'];
    iconsData?: IconsLinksListProps['iconsData'];
    cta?: CtaProps;
    promoCodeConfig?: PromoCodeProps;
    additionalText?: string;
    informationText?: string;
    buttonsConfig?: ButtonsFactoryProps;
}

const IconPointerListContent: FC<IconPointerListContentProps> = ({
    isLogotypeVisible,
    buttonsConfig,
    logotypeUrl,
    contentTextConfig,
    description,
    listData,
    iconsData,
    cta,
    promoCodeConfig,
    additionalText,
    informationText,
}) => (
    <div className="w-full max-w-[22rem] text-center md:text-left lg:max-w-[556px]">
        {!!isLogotypeVisible && logotypeUrl && (
            <ImageLink
                src={logotypeUrl}
                alt="Logotyp"
                className="mb-4 block"
                width={88}
                height={48}
            />
        )}

        <ContentText
            {...contentTextConfig}
            className={cn(
                'text-center md:text-left',
                contentTextConfig.className,
            )}
        />

        {!!description && <RichText field={description} />}

        <IconPointerList listData={listData} />

        {!!iconsData && (
            <IconsLinksList
                variant="horizontalLeft"
                iconsData={iconsData}
                className="mt-8 sm:mt-10"
            />
        )}

        {!!promoCodeConfig && (
            <PromoCode
                className="pt-8 md:pt-10"
                {...promoCodeConfig}
            />
        )}

        {!!cta && (
            <Cta
                className="!pt-8 md:!pt-10"
                {...cta}
            />
        )}

        {!!additionalText && (
            <ShowMore
                lines={1}
                className={cn('pt-6 text-3xs')}
            >
                {parse(additionalText)}
            </ShowMore>
        )}

        {!!informationText && (
            <P
                size={'2xs'}
                className="mt-8 text-typo-secondary"
            >
                {parse(informationText)}
            </P>
        )}

        {!!buttonsConfig && (
            <ButtonsFactory
                className="mt-8 w-auto flex-nowrap md:mt-10"
                {...buttonsConfig}
            />
        )}
    </div>
);

export default IconPointerListContent;
